import { ref, computed, watch } from 'vue';
import { isFolderItem, itemKey } from './useItemTypeGuards';

const STORAGE_KEY_PREFIX = 'exa_share_basket_v1_user_';

export const useShareBasket = (folders, userId) => {
  const buildStorageKey = () => {
    const id = String(userId.value ?? '');
    return id ? `${STORAGE_KEY_PREFIX}${id}` : '';
  };

  const restoreFromStorage = () => {
    const key = buildStorageKey();
    if (!key || typeof window === 'undefined' || !window.sessionStorage) {
      return new Map();
    }
    try {
      const raw = window.sessionStorage.getItem(key);
      if (!raw) return new Map();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Map();
      const map = new Map();
      parsed.forEach((entry) => {
        if (entry && (entry.id || entry.id === 0)) {
          // typeが無い旧エントリはフォルダ選択リストへ入る経路が無かったためvideoで確定
          const type = entry.type === 'folder' ? 'folder' : 'video';
          map.set(`${type}:${entry.id}`, {
            id: entry.id,
            type,
            name: entry.name || '',
            folderId: entry.folderId ?? null,
            folderName: entry.folderName || '',
            versionCount: Number(entry.versionCount) || 1,
            canDeleteSource: !!entry.canDeleteSource,
            canEditTags: !!entry.canEditTags,
            aiAnalysisStatus: entry.aiAnalysisStatus ?? null,
          });
        }
      });
      return map;
    } catch (error) {
      return new Map();
    }
  };

  const persistToStorage = (map) => {
    const key = buildStorageKey();
    if (!key || typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }
    try {
      const serialized = JSON.stringify(Array.from(map.values()));
      window.sessionStorage.setItem(key, serialized);
    } catch (error) {
      // sessionStorage 不可用時は黙って失敗（メモリ動作は継続）
    }
  };

  const basketMap = ref(new Map());

  const stopRestoreWatch = watch(
    () => userId.value,
    (next, prev) => {
      if (next && !prev) {
        basketMap.value = restoreFromStorage();
        stopRestoreWatch();
      }
    },
    { immediate: true }
  );

  watch(
    basketMap,
    (next) => {
      if (!buildStorageKey()) return;
      persistToStorage(next);
    },
    { deep: false, flush: 'sync' }
  );

  const resolveFolderPath = (folderId) => {
    if (!folderId) return '';
    const list = Array.isArray(folders.value) ? folders.value : [];
    const map = {};
    list.forEach((f) => { map[String(f.id)] = f; });

    const segments = [];
    let current = map[String(folderId)];
    while (current) {
      segments.unshift(current.name);
      const pid = current.parent_id;
      if (!pid || !map[String(pid)]) break;
      current = map[String(pid)];
    }
    return segments.join(' / ');
  };

  const buildEntry = (item) => {
    if (isFolderItem(item)) {
      // フォルダはゴミ箱移動・タグ付けの対象にできない（フォルダ削除はuseFolderBulkDeleteの一括削除導線で別途対応）
      return {
        id: item.id,
        type: 'folder',
        name: item.name || '',
        folderId: null,
        folderName: item.folder_path || '',
        versionCount: 1,
        canDeleteSource: false,
        canEditTags: false,
        aiAnalysisStatus: null,
      };
    }
    return {
      id: item.id,
      type: 'video',
      name: item.name || item.original_name || '',
      folderId: item.folder_id,
      folderName: resolveFolderPath(item.folder_id),
      versionCount: Number(item.version_count) || 1,
      canDeleteSource: !!item.can_delete_source,
      canEditTags: !!item.can_edit_tags,
      // 一括AI解析モーダルで「新規 / 再解析」を数えるための解析状態 (一覧項目の ai_analysis_status)
      aiAnalysisStatus: item.ai_analysis_status ?? null,
    };
  };

  const addToBasket = (item) => {
    const next = new Map(basketMap.value);
    next.set(itemKey(item), buildEntry(item));
    basketMap.value = next;
  };

  // type省略時は既存呼び出し元（すべて動画対象）との互換のためvideoとして扱う
  const removeFromBasket = (id, type = 'video') => {
    const next = new Map(basketMap.value);
    next.delete(`${type}:${id}`);
    basketMap.value = next;
  };

  const toggleBasket = (item) => {
    const key = itemKey(item);
    if (basketMap.value.has(key)) {
      removeFromBasket(item.id, isFolderItem(item) ? 'folder' : 'video');
    } else {
      addToBasket(item);
    }
  };

  const setBasketItems = (items, { append = false } = {}) => {
    const next = append ? new Map(basketMap.value) : new Map();
    items.forEach((item) => {
      next.set(itemKey(item), buildEntry(item));
    });
    basketMap.value = next;
  };

  // type省略時は既存呼び出し元（すべて動画対象）との互換のためvideoとして扱う
  const isInBasket = (id, type = 'video') => {
    return basketMap.value.has(`${type}:${id}`);
  };

  const clearBasket = () => {
    basketMap.value = new Map();
  };

  const basketCount = computed(() => basketMap.value.size);

  const basketVideoIds = computed(() =>
    Array.from(basketMap.value.values())
      .filter((entry) => entry.type === 'video')
      .map((entry) => Number(entry.id))
  );

  const basketFolderIds = computed(() =>
    Array.from(basketMap.value.values())
      .filter((entry) => entry.type === 'folder')
      .map((entry) => Number(entry.id))
  );

  const basketFileCount = computed(() => basketVideoIds.value.length);

  const basketFolderCount = computed(() => basketFolderIds.value.length);

  const basketItemsList = computed(() =>
    Array.from(basketMap.value.values()).sort((a, b) => Number(a.id) - Number(b.id))
  );

  const basketFileItemsList = computed(() =>
    basketItemsList.value.filter((entry) => entry.type === 'video')
  );

  return {
    basketCount,
    basketVideoIds,
    basketFolderIds,
    basketFileCount,
    basketFolderCount,
    basketItemsList,
    basketFileItemsList,
    addToBasket,
    removeFromBasket,
    toggleBasket,
    setBasketItems,
    isInBasket,
    clearBasket,
  };
};
